import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { heroSlides } from "../../data/mock";

const fallbackSlide = {
  id: "hero-fallback",
  image: "",
  title: "Pâtisserie Ngoussi",
  subtitle: "Des créations faites maison pour vos moments précieux",
};

const HeroSection = () => {
  const availableSlides = Array.isArray(heroSlides)
    ? heroSlides.filter((item) => item && typeof item === "object")
    : [];
  const slides = availableSlides.length > 0
    ? availableSlides.map((item, index) => ({
      id: item.id || `hero-${index + 1}`,
      image: typeof item.image === "string" ? item.image : "",
      title: typeof item.title === "string" ? item.title : fallbackSlide.title,
      subtitle: typeof item.subtitle === "string" ? item.subtitle : fallbackSlide.subtitle,
    }))
    : [fallbackSlide];
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = slides[currentSlide] || fallbackSlide;

  return (
    <section className="relative h-screen overflow-hidden">
      {slides.map((s, index) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="absolute inset-0 bg-black/40 z-10" />
          {s.image && <img src={s.image} alt={s.title || ""} className="w-full h-full object-cover" />}
        </div>
      ))}

      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-[#D4AF37]/20 blur-3xl z-10" />
      <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-[#E5007D]/20 blur-3xl z-10" />

      <div className="relative z-20 h-full flex items-center justify-center">
        <div className="text-center px-4 max-w-4xl mx-auto">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-12 bg-[#D4AF37]" />
            </div>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4 leading-tight">
              {slide.title || fallbackSlide.title}
            </h1>
            <p className="font-playfair text-2xl sm:text-3xl md:text-5xl text-[#D4AF37] italic font-light">
              {slide.subtitle || fallbackSlide.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/commande"
                className="px-8 py-4 bg-[#E5007D] text-white rounded-full font-medium text-sm tracking-wide hover:bg-[#c70069] transition-all duration-300 hover:shadow-lg hover:shadow-[#E5007D]/30 flex items-center gap-2"
              >
                Commander un gâteau
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/realisations"
                className="px-8 py-4 border-2 border-white/60 text-white rounded-full font-medium text-sm tracking-wide hover:bg-white/10 transition-all duration-300"
              >
                Voir mes réalisations
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((s, index) => (
          <button
            key={s.id || `hero-${index}`}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide
                ? "w-8 h-3 bg-[#E5007D]"
                : "w-3 h-3 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
